/**
 * Buffering methods support these modes.
 */
export enum BufferingMode {
    ResolveWhenSingleFrame = 'resolveWhenSingleFrame',
    ResolveWhenCanPlay = 'resolveWhenCanPlay',
    PrepareForPlayback = 'prepareForPlayback',
}

/**
 * luminary.seek supports those modes
 */
export enum SeekMode {
    ResolveWhenSingleFrame = 'resolveWhenSingleFrame',
    ResolveWhenCanPlay = 'resolveWhenCanPlay',
}

export enum CreationType {
    Animation = 'animation',
    Graphic = 'graphic',
}

export enum DynamicStylingCreationLevel {
    Object = 'object',
    Line = 'line',
    Word = 'word',
    Highlight = 'highlight',
}

export enum GraphicClassTypes {
    BoxGraphic = 'BoxGraphic',
    BoxBorderGraphic = 'BoxBorderGraphic',
    CircleGraphic = 'CircleGraphic',
}

export const AllGraphicClassTypes = Object.values(GraphicClassTypes);

export enum LicenseEnum {
    CC0 = 'CC0',
    BY = 'BY',
    USER_PRIVATE = 'user_private',
}

export enum ValueMapMode {
    GET_REFERENCE_VALUES = 'reference',
    GET_PRIMITIVE_VALUES = 'primitive',
    GET_ALL_VALUES_AS_PRIMITIVE = 'allAsPrimitive',
}

export enum WatermarkPositions {
    TopLeft = 'top_left',
    BottomLeft = 'bottom_left',
    TopRight = 'top_right',
    BottomRight = 'bottom_right',
    BottomCenter = 'bottom_center',
    Center = 'center',
    TopCenter = 'top_center',
    CenterLeft = 'center_left',
    CenterRight = 'center_right',
}

export enum ColorFormat {
    /** hex string, eg "#00FF00" */
    HEX,
    /** rgb array, eg [0, 255, 0] */
    RGB,
}

export enum TextAlignment {
    LEFT = 'left',
    CENTER = 'center',
    RIGHT = 'right',
}

export const TextDirection = {
    LTR: 'ltr',
    RTL: 'rtl',
} as const;
// eslint-disable-next-line no-redeclare -- TypeScript pattern: const + type with same name
export type TextDirection = (typeof TextDirection)[keyof typeof TextDirection];

// ------------------------------ CANVAS TEXT RENDERING ENUMS ------------------------------

export enum DynamicGraphic {
    circle = 'circle',
    box = 'box',
    boxBorder = 'boxBorder',
}

export enum DynamicLevel {
    object = 'object',
    line = 'line',
    word = 'word',
    highlight = 'highlight',
}

export type DynamicLevels =
    | DynamicLevel.object
    | DynamicLevel.line
    | DynamicLevel.word
    | DynamicLevel.highlight;

export enum DynamicInferredHeight {
    /**
     * The maximum height out of all objects from the specified level
     */
    maxHeight = 'maxHeight',
    /**
     * The height of the first object from the specified level
     */
    height = 'height',
    /**
     * Not supported for animating text properties
     */
    gapless = 'gapless',
    /**
     * Not supported for animating text properties
     */
    heightWithoutDescent = 'heightWithoutDescent',
}

export enum DynamicInferredWidth {
    maxWidth = 'maxWidth',
    width = 'width',
    gaplessWidth = 'gaplessWidth',
}

export const DynamicInferredDimensions = { ...DynamicInferredWidth, ...DynamicInferredHeight };
// eslint-disable-next-line no-redeclare
export type DynamicInferredDimensions = DynamicInferredWidth | DynamicInferredHeight;

export type DynamicHeight = number | DynamicInferredHeight;
export type DynamicWidth = number | DynamicInferredWidth;

export enum DynamicXAlign {
    left = 'left',
    center = 'center',
    right = 'right',
}

export enum DynamicYAlign {
    maxTop = 'maxTop',
    top = 'top',
    center = 'center',
    averageCenter = 'averageCenter',
    baseline = 'baseline',
    bottom = 'bottom',
    maxBottom = 'maxBottom',
}

export enum DynamicXDirection {
    left = 'left', // to the left
    right = 'right', // to the right
    zigZagLeft = 'zigZagLeft',
    zigZagRight = 'zigZagRight',
    center = 'center',
}

export enum DynamicYDirection {
    top = 'top', // to the top, this means the direction goes up
    bottom = 'bottom', // to the bottom, this means the direction goes down
    zigZagTop = 'zigZagTop',
    zigZagBottom = 'zigZagBottom',
    center = 'center',
}

export enum AssetCreatedFrom {
    /**
     * Asset is part of scene design
     */
    SCENE_DESIGN = 'SCENE_DESIGN',
    /**
     * Asset is not part of the scene design and can be freely resized and respositioned
     */
    STICKER = 'STICKER',
}

export enum SceneImageRenderMode {
    FRAME = 'frame',
    ALL_ASSETS_VISIBLE = 'allAssetsVisible',
}
